/**
 * Placeholder scanning for campaign content, shared by the builder and the API (PREC-4153).
 *
 * A campaign step's message, subject and invite note carry `{varName|Label}` tokens. These
 * helpers read them out and compare them against the keys a campaign may reference. They moved
 * out of `apps/web` when the campaign rulebook became shared: `apps/api` cannot import from
 * `apps/web`, and a second copy of "which tokens are in this text" is how two surfaces start
 * disagreeing about whether a campaign can send.
 *
 * The TinyMCE-facing halves (`invalidPlaceholderStyle`, `isValidPlaceholder`) stayed in the web
 * app — they are about rendering the editor's pills, not about whether a campaign is valid.
 */

/**
 * Extracts placeholder variable names from raw content string (e.g. message or subject).
 * Content uses `{varName|label}` or `{varName}` format.
 * Returns an array of variable names (the part before `|`).
 */
export const extractPlaceholderVarNames = (content: string): string[] => {
	const regex = /\{([^}]+)\}/g;
	const varNames: string[] = [];
	let match;

	while ((match = regex.exec(content)) !== null) {
		const [varName] = match[1].split("|");

		varNames.push(varName);
	}

	return varNames;
};

/**
 * Returns the deduplicated variable names in `content` that are not in `validKeys`.
 */
export const getInvalidPlaceholders = (content: string, validKeys: string[]): string[] => {
	const varNames = extractPlaceholderVarNames(content);

	return [...new Set(varNames.filter((varName) => !validKeys.includes(varName)))];
};

/**
 * Checks if content contains any invalid placeholders.
 * Returns true if there are invalid placeholders.
 */
export const hasInvalidPlaceholders = (content: string, validKeys: string[]): boolean => {
	return getInvalidPlaceholders(content, validKeys).length > 0;
};

/**
 * Checks if any node in the list uses the {signature} placeholder variable
 * in its message, subject, or premiumMessage fields.
 */
export const nodesContainSignatureVariable = (
	nodes: { data?: { message?: string; subject?: string; premiumMessage?: string } }[]
): boolean => {
	for (const node of nodes) {
		const fields = [node.data?.message, node.data?.subject, node.data?.premiumMessage];

		for (const field of fields) {
			if (field) {
				const varNames = extractPlaceholderVarNames(field);

				if (varNames.includes("signature")) {
					return true;
				}
			}
		}
	}

	return false;
};
